// Selected results shown on the About page and referenced from service pages.
//
// All engagements are under NDA: no client names, cities, or identifying
// details. Results are described qualitatively — add a metric to `result`
// only if the client has agreed to share it.

export interface CaseStudy {
  id: string;
  /** Short industry tag shown on the card, e.g. "Professional Services". */
  industry: string;
  title: string;
  challenge: string;
  built: string;
  /** Headline result. Keep it factual; no numbers unless the client approved them. */
  result: string;
  /** Tools shown as small chips on the card. */
  tools: string[];
  /** True only for template copy that hasn't been replaced with a real engagement. */
  placeholder: boolean;
}

export const CASE_STUDIES: CaseStudy[] = [
  {
    id: 'ocr-document-intake',
    industry: 'Accounting · Professional Services',
    title: 'AI document intake for an Ontario accounting firm',
    challenge:
      'Client documents — T-slips, invoices, statements, spreadsheets — arrived in one SharePoint folder and staff renamed and filed each one by hand into the right client, entity, and year folder.',
    built:
      'A Power Automate flow that detects new files, runs OCR, and uses Azure OpenAI to return a structured JSON of client, entity, document type, year, and period. It renames the file to the firm’s naming convention, creates the SharePoint folder path if it doesn’t exist, moves the file, and writes every action to a processing log. Anything below a confidence threshold lands in a review queue instead of being filed.',
    result:
      'Documents are named, filed, and logged automatically; staff only touch the review queue. Every move is auditable from the log.',
    tools: ['Power Automate', 'Azure OpenAI', 'SharePoint', 'Azure AI Document Intelligence'],
    placeholder: false,
  },
  {
    id: 'policy-copilot',
    industry: 'Internal Operations',
    title: 'Policy Q&A Copilot grounded on company documents',
    challenge:
      'Staff questions about company policies — leave, expenses, IT and security rules — went to a handful of people by email and chat, and answers varied depending on who replied.',
    built:
      'A Copilot Studio agent grounded on the organization’s own policy documents in SharePoint. It answers in plain language, points to the source policy, and hands off to a person when a question falls outside the documents. Nothing is answered from outside the approved sources.',
    result:
      'Employees get consistent, sourced policy answers on demand, and the people who used to field those questions get their time back.',
    tools: ['Copilot Studio', 'SharePoint', 'Microsoft 365'],
    placeholder: false,
  },
  {
    id: 'm365-tenant-onboarding',
    industry: 'Small Business & Nonprofit',
    title: 'Microsoft 365 tenant onboarding',
    challenge:
      'Small teams working from personal email accounts, with no shared files, no Teams, and no idea which Microsoft licensing they qualified for.',
    built:
      'New Microsoft 365 tenants on the right licensing for each organization type, custom-domain email with DNS cut over, Teams and SharePoint set up, migrations from previous providers where needed, and a plain-English handover guide.',
    result:
      'Professional email, shared files, and Teams live within days — and a tenant that later became the foundation for automation.',
    tools: ['Microsoft 365', 'Exchange Online', 'SharePoint', 'Teams'],
    placeholder: false,
  },
];

export function getCaseStudy(id: string): CaseStudy | undefined {
  return CASE_STUDIES.find(c => c.id === id);
}
